import {
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  LockOutlined,
  MailOutlined,
  QqOutlined,
  SafetyCertificateOutlined,
  UserAddOutlined,
  UserOutlined
} from "@ant-design/icons";
import {
  Alert,
  App,
  Avatar,
  Button,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  QRCode,
  Select,
  Space,
  Switch,
  Tag
} from "antd";
import { useEffect, useState } from "react";
import {
  bindEmail,
  bindQq,
  createAdminAccount,
  deleteAdminAccount,
  disableTotp,
  enableTotp,
  listAdminAccounts,
  sendBindingEmailCode,
  setToken,
  setupTotp,
  unbindQq,
  updateAdminAccount
} from "../services/api";
import type { AdminAccount } from "../services/api";
import type { SessionUser } from "../types";

type TotpSetup = { secret: string; otpauthUrl: string };
type CreateAdminValues = {
  username: string;
  email?: string;
  role: "OPERATOR" | "AUDITOR";
  password: string;
  confirmPassword: string;
  currentPassword: string;
  totp?: string;
};
type EditAdminValues = {
  username: string;
  email?: string;
  role: "OPERATOR" | "AUDITOR";
  password?: string;
  confirmPassword?: string;
  currentPassword: string;
  totp?: string;
};
type DeleteAdminValues = {
  currentPassword: string;
  totp?: string;
};

const roleLabels: Record<AdminAccount["role"], string> = {
  OWNER: "Owner",
  OPERATOR: "运营管理员",
  AUDITOR: "只读审计员"
};

export function AccountSettings({
  open,
  user,
  onClose,
  onUserChange
}: {
  open: boolean;
  user: SessionUser;
  onClose: () => void;
  onUserChange: (user: SessionUser) => void;
}) {
  const { message } = App.useApp();
  const [working, setWorking] = useState(false);
  const [totpSetup, setTotpSetup] = useState<TotpSetup | null>(null);
  const [showDisable, setShowDisable] = useState(false);
  const [emailSeconds, setEmailSeconds] = useState(0);
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [editingAccount, setEditingAccount] = useState<AdminAccount | null>(null);
  const [deletingAccount, setDeletingAccount] = useState<AdminAccount | null>(null);
  const [emailForm] = Form.useForm<{ email: string; code: string }>();
  const [createAdminForm] = Form.useForm<CreateAdminValues>();
  const [editAdminForm] = Form.useForm<EditAdminValues>();
  const [deleteAdminForm] = Form.useForm<DeleteAdminValues>();

  useEffect(() => {
    if (emailSeconds <= 0) return;
    const timer = window.setInterval(() => setEmailSeconds((value) => Math.max(0, value - 1)), 1_000);
    return () => window.clearInterval(timer);
  }, [emailSeconds]);

  useEffect(() => {
    if (!open || user.role !== "OWNER") return;
    let active = true;
    setAccountsLoading(true);
    listAdminAccounts()
      .then((result) => {
        if (active) setAccounts(result.accounts);
      })
      .catch((error) => {
        if (active) {
          void message.error(error instanceof Error ? error.message : "账号列表加载失败");
        }
      })
      .finally(() => {
        if (active) setAccountsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [message, open, user.role]);

  const updateSession = (result: { token?: string; user: SessionUser }) => {
    if (result.token) setToken(result.token);
    onUserChange(result.user);
  };

  return (
    <Drawer
      title="账号与安全"
      size={680}
      open={open}
      onClose={() => {
        setEditingAccount(null);
        setDeletingAccount(null);
        onClose();
      }}
      destroyOnHidden
    >
      <div className="account-identity">
        <Avatar size={64} src={user.qqAvatarUrl ?? undefined}>
          {user.username.slice(0, 1).toUpperCase()}
        </Avatar>
        <div>
          <h2>{user.username}</h2>
          <Space>
            <Tag color="blue">{user.role}</Tag>
            <span>{user.email ?? "尚未绑定邮箱"}</span>
          </Space>
        </div>
      </div>

      {user.role === "OWNER" ? (
        <>
          <Divider titlePlacement="start">后台账号管理</Divider>
          <Alert
            type="info"
            showIcon
            title="公开注册已关闭"
            description="只有 Owner 能在这里添加运营管理员或只读审计员。创建账号不会发送邮件。"
          />
          <List
            className="admin-account-list"
            bordered
            loading={accountsLoading}
            dataSource={accounts}
            locale={{ emptyText: "暂无后台账号" }}
            renderItem={(account) => {
              const protectedAccount = account.id === user.id || account.role === "OWNER";
              return (
              <List.Item
                actions={[
                  <Button
                    key="edit"
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    disabled={protectedAccount}
                    title={protectedAccount ? "当前账号或 Owner 账号请使用个人设置修改" : "编辑账号"}
                    onClick={() => {
                      setEditingAccount(account);
                      editAdminForm.setFieldsValue({
                        username: account.username,
                        email: account.email ?? "",
                        role: account.role === "OPERATOR" ? "OPERATOR" : "AUDITOR",
                        password: undefined,
                        confirmPassword: undefined,
                        currentPassword: "",
                        totp: ""
                      });
                    }}
                  >
                    编辑
                  </Button>,
                  <Button
                    key="delete"
                    type="link"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={protectedAccount}
                    title={protectedAccount ? "不能删除当前账号或 Owner 账号" : "删除账号"}
                    onClick={() => {
                      setDeletingAccount(account);
                      deleteAdminForm.resetFields();
                    }}
                  >
                    删除
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={(
                    <Avatar>
                      {account.username.slice(0, 1).toUpperCase()}
                    </Avatar>
                  )}
                  title={(
                    <Space wrap>
                      <strong>{account.username}</strong>
                      <Tag color={account.role === "OWNER" ? "gold" : account.role === "OPERATOR" ? "blue" : "default"}>
                        {roleLabels[account.role]}
                      </Tag>
                      {account.totpEnabled ? <Tag color="green">已启用动态码</Tag> : null}
                    </Space>
                  )}
                  description={account.email ?? "未绑定邮箱"}
                />
              </List.Item>
              );
            }}
          />
          <Form<CreateAdminValues>
            className="admin-account-form"
            form={createAdminForm}
            layout="vertical"
            initialValues={{ role: "AUDITOR" }}
            scrollToFirstError={{ focus: true }}
            onFinish={async (values) => {
              setWorking(true);
              try {
                const result = await createAdminAccount({
                  username: values.username,
                  email: values.email?.trim() || undefined,
                  password: values.password,
                  role: values.role,
                  currentPassword: values.currentPassword,
                  totp: values.totp
                });
                setAccounts((current) => [...current, result.account]);
                createAdminForm.resetFields();
                createAdminForm.setFieldValue("role", "AUDITOR");
                void message.success(`后台账号 ${result.account.username} 已创建`);
              } catch (error) {
                void message.error(error instanceof Error ? error.message : "账号创建失败");
              } finally {
                setWorking(false);
              }
            }}
          >
            <h3><UserAddOutlined /> 添加后台账号</h3>
            <div className="admin-account-form__grid">
              <Form.Item
                label="操作员名称"
                name="username"
                rules={[
                  { required: true, message: "请输入操作员名称" },
                  { min: 3, max: 64 },
                  { pattern: /^[\p{L}\p{N}_-]+$/u, message: "只能使用中英文、数字、下划线或短横线" }
                ]}
              >
                <Input prefix={<UserOutlined />} autoComplete="off" />
              </Form.Item>
              <Form.Item label="角色" name="role" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: "AUDITOR", label: "只读审计员" },
                    { value: "OPERATOR", label: "运营管理员" }
                  ]}
                />
              </Form.Item>
            </div>
            <Form.Item label="邮箱（可选，可用于登录和找回密码）" name="email" rules={[{ type: "email" }]}>
              <Input prefix={<MailOutlined />} autoComplete="off" />
            </Form.Item>
            <div className="admin-account-form__grid">
              <Form.Item
                label="新账号密码"
                name="password"
                rules={[{ required: true }, { min: 12, message: "密码至少 12 位" }]}
              >
                <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
              </Form.Item>
              <Form.Item
                label="确认新账号密码"
                name="confirmPassword"
                dependencies={["password"]}
                rules={[
                  { required: true },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      return !value || getFieldValue("password") === value
                        ? Promise.resolve()
                        : Promise.reject(new Error("两次输入的密码不一致"));
                    }
                  })
                ]}
              >
                <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
              </Form.Item>
            </div>
            <Form.Item
              label="当前 Owner 密码"
              name="currentPassword"
              rules={[{ required: true, message: "请输入当前 Owner 密码确认操作" }]}
            >
              <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
            </Form.Item>
            {user.totpEnabled ? (
              <Form.Item
                label="当前动态验证码"
                name="totp"
                rules={[{ required: true }, { len: 6, message: "请输入 6 位动态码" }]}
              >
                <Input
                  prefix={<SafetyCertificateOutlined />}
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                />
              </Form.Item>
            ) : null}
            <Button type="primary" htmlType="submit" icon={<UserAddOutlined />} loading={working}>
              创建后台账号
            </Button>
          </Form>
        </>
      ) : null}

      <Divider titlePlacement="start">邮箱登录</Divider>
      <Form
        form={emailForm}
        layout="vertical"
        initialValues={{ email: user.email ?? "" }}
        onFinish={async (values) => {
          setWorking(true);
          try {
            updateSession(await bindEmail(values.email, values.code));
            void message.success("邮箱已验证并绑定");
            emailForm.setFieldValue("code", "");
          } catch (error) {
            void message.error(error instanceof Error ? error.message : "邮箱绑定失败");
          } finally {
            setWorking(false);
          }
        }}
      >
        <Form.Item label="邮箱" name="email" rules={[{ required: true }, { type: "email" }]}>
          <Input prefix={<MailOutlined />} />
        </Form.Item>
        <Form.Item label="邮箱验证码" required>
          <div className="verification-row">
            <Form.Item name="code" noStyle rules={[{ required: true }, { len: 6 }]}>
              <Input prefix={<KeyOutlined />} inputMode="numeric" maxLength={6} />
            </Form.Item>
            <Button
              disabled={emailSeconds > 0}
              onClick={async () => {
                try {
                  const email = String(await emailForm.validateFields(["email"]).then((value) => value.email));
                  const result = await sendBindingEmailCode(email);
                  setEmailSeconds(60);
                  void message.success(result.message);
                } catch (error) {
                  if (error instanceof Error) void message.error(error.message);
                }
              }}
            >
              {emailSeconds ? `${emailSeconds}s` : "发送验证码"}
            </Button>
          </div>
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={working}>保存邮箱</Button>
      </Form>

      <Divider titlePlacement="start">QQ 头像</Divider>
      {user.qq ? (
        <Descriptions
          size="small"
          column={1}
          items={[
            { key: "qq", label: "QQ", children: user.qq },
            { key: "nickname", label: "昵称", children: user.qqNickname ?? "—" }
          ]}
        />
      ) : (
        <Alert type="info" showIcon title="绑定 QQ 后，将使用 UAPI 返回的真实 QQ 头像。" />
      )}
      <Form<{ qq: string }>
        layout="inline"
        className="account-inline-form"
        initialValues={{ qq: user.qq ?? "" }}
        onFinish={async ({ qq }) => {
          setWorking(true);
          try {
            updateSession(await bindQq(qq));
            void message.success("QQ 资料与头像已同步");
          } catch (error) {
            void message.error(error instanceof Error ? error.message : "QQ 绑定失败");
          } finally {
            setWorking(false);
          }
        }}
      >
        <Form.Item name="qq" rules={[{ required: true }, { pattern: /^\d{5,12}$/ }]}>
          <Input prefix={<QqOutlined />} placeholder="输入 QQ 号" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={working}>
          {user.qq ? "重新同步" : "绑定 QQ"}
        </Button>
        {user.qq ? (
          <Popconfirm
            title="解除 QQ 绑定？"
            description="解除后将恢复使用文字头像。"
            onConfirm={async () => {
              setWorking(true);
              try {
                updateSession(await unbindQq());
                void message.success("QQ 已解除绑定");
              } finally {
                setWorking(false);
              }
            }}
          >
            <Button danger>解绑</Button>
          </Popconfirm>
        ) : null}
      </Form>

      <Divider titlePlacement="start">动态验证码</Divider>
      <div className="security-toggle">
        <div>
          <b>登录动态验证码</b>
          <p>{user.totpEnabled ? "已开启，每次登录需要 6 位动态码。" : "当前关闭，可按账号自行开启。"}</p>
        </div>
        <Switch
          checked={user.totpEnabled}
          checkedChildren="已开启"
          unCheckedChildren="已关闭"
          onChange={async (checked) => {
            if (checked) {
              setWorking(true);
              try {
                setTotpSetup(await setupTotp());
              } catch (error) {
                void message.error(error instanceof Error ? error.message : "动态验证码初始化失败");
              } finally {
                setWorking(false);
              }
            } else {
              setShowDisable(true);
            }
          }}
        />
      </div>

      {totpSetup ? (
        <div className="totp-setup">
          <Alert
            type="warning"
            showIcon
            title="请先用验证器扫描二维码，再输入当前显示的 6 位动态码完成开启。"
          />
          <QRCode value={totpSetup.otpauthUrl} size={184} />
          <code>{totpSetup.secret}</code>
          <Form<{ totp: string }>
            layout="inline"
            onFinish={async ({ totp }) => {
              setWorking(true);
              try {
                updateSession(await enableTotp(totpSetup.secret, totp));
                setTotpSetup(null);
                void message.success("动态验证码已开启");
              } catch (error) {
                void message.error(error instanceof Error ? error.message : "开启失败");
              } finally {
                setWorking(false);
              }
            }}
          >
            <Form.Item name="totp" rules={[{ required: true }, { len: 6 }]}>
              <Input prefix={<SafetyCertificateOutlined />} inputMode="numeric" maxLength={6} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={working}>确认开启</Button>
          </Form>
        </div>
      ) : null}

      {showDisable ? (
        <Form<{ password: string; totp: string }>
          className="totp-disable"
          layout="vertical"
          onFinish={async ({ password, totp }) => {
            setWorking(true);
            try {
              updateSession(await disableTotp(password, totp));
              setShowDisable(false);
              void message.success("动态验证码已关闭");
            } catch (error) {
              void message.error(error instanceof Error ? error.message : "关闭失败");
            } finally {
              setWorking(false);
            }
          }}
        >
          <Alert
            type="warning"
            showIcon
            title="关闭后普通登录不再需要动态码；主账号换绑等关键操作仍会要求先重新开启。"
          />
          <Form.Item label="当前密码" name="password" rules={[{ required: true }]}>
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
          <Form.Item label="当前动态码" name="totp" rules={[{ required: true }, { len: 6 }]}>
            <Input prefix={<SafetyCertificateOutlined />} inputMode="numeric" maxLength={6} />
          </Form.Item>
          <Space>
            <Button danger type="primary" htmlType="submit" loading={working}>确认关闭</Button>
            <Button onClick={() => setShowDisable(false)}>取消</Button>
          </Space>
        </Form>
      ) : null}

      <Modal
        title={`编辑后台账号${editingAccount ? `：${editingAccount.username}` : ""}`}
        open={Boolean(editingAccount)}
        confirmLoading={working}
        okText="保存修改"
        cancelText="取消"
        destroyOnHidden
        onOk={() => editAdminForm.submit()}
        onCancel={() => {
          setEditingAccount(null);
          editAdminForm.resetFields();
        }}
      >
        <Form<EditAdminValues>
          form={editAdminForm}
          layout="vertical"
          clearOnDestroy
          scrollToFirstError={{ focus: true }}
          onFinish={async (values) => {
            if (!editingAccount) return;
            setWorking(true);
            try {
              const result = await updateAdminAccount(editingAccount.id, {
                username: values.username,
                email: values.email?.trim() || undefined,
                role: values.role,
                password: values.password || undefined,
                currentPassword: values.currentPassword,
                totp: values.totp
              });
              setAccounts((current) => current.map((account) => (
                account.id === result.account.id ? result.account : account
              )));
              setEditingAccount(null);
              editAdminForm.resetFields();
              void message.success(`后台账号 ${result.account.username} 已更新`);
            } catch (error) {
              void message.error(error instanceof Error ? error.message : "账号更新失败");
            } finally {
              setWorking(false);
            }
          }}
        >
          <Alert
            type="info"
            showIcon
            title="修改后该账号的现有登录会立即失效"
            description="留空新密码表示保持原密码不变。"
          />
          <div className="admin-account-modal__grid">
            <Form.Item
              label="操作员名称"
              name="username"
              rules={[
                { required: true, message: "请输入操作员名称" },
                { min: 3, max: 64 },
                { pattern: /^[\p{L}\p{N}_-]+$/u, message: "只能使用中英文、数字、下划线或短横线" }
              ]}
            >
              <Input prefix={<UserOutlined />} autoComplete="off" />
            </Form.Item>
            <Form.Item label="角色" name="role" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: "AUDITOR", label: "只读审计员" },
                  { value: "OPERATOR", label: "运营管理员" }
                ]}
              />
            </Form.Item>
          </div>
          <Form.Item label="邮箱（可选）" name="email" rules={[{ type: "email" }]}>
            <Input prefix={<MailOutlined />} autoComplete="off" />
          </Form.Item>
          <div className="admin-account-modal__grid">
            <Form.Item label="新密码（留空不修改）" name="password" rules={[{ min: 12, message: "密码至少 12 位" }]}>
              <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              label="确认新密码"
              name="confirmPassword"
              dependencies={["password"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    return !value || getFieldValue("password") === value
                      ? Promise.resolve()
                      : Promise.reject(new Error("两次输入的密码不一致"));
                  }
                })
              ]}
            >
              <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
            </Form.Item>
          </div>
          <Form.Item
            label="当前 Owner 密码"
            name="currentPassword"
            rules={[{ required: true, message: "请输入当前 Owner 密码确认操作" }]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
          </Form.Item>
          {user.totpEnabled ? (
            <Form.Item
              label="当前动态验证码"
              name="totp"
              rules={[{ required: true }, { len: 6, message: "请输入 6 位动态码" }]}
            >
              <Input
                prefix={<SafetyCertificateOutlined />}
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
              />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>

      <Modal
        title={`删除后台账号${deletingAccount ? `：${deletingAccount.username}` : ""}`}
        open={Boolean(deletingAccount)}
        confirmLoading={working}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
        destroyOnHidden
        onOk={() => deleteAdminForm.submit()}
        onCancel={() => {
          setDeletingAccount(null);
          deleteAdminForm.resetFields();
        }}
      >
        <Alert
          type="error"
          showIcon
          title="删除后该账号将立即无法登录"
          description="该操作不会删除已有审计记录，且不能撤销。"
        />
        <Form<DeleteAdminValues>
          className="admin-account-delete-form"
          form={deleteAdminForm}
          layout="vertical"
          clearOnDestroy
          onFinish={async (values) => {
            if (!deletingAccount) return;
            setWorking(true);
            try {
              await deleteAdminAccount(deletingAccount.id, values);
              setAccounts((current) => current.filter((account) => account.id !== deletingAccount.id));
              void message.success(`后台账号 ${deletingAccount.username} 已删除`);
              setDeletingAccount(null);
              deleteAdminForm.resetFields();
            } catch (error) {
              void message.error(error instanceof Error ? error.message : "账号删除失败");
            } finally {
              setWorking(false);
            }
          }}
        >
          <Form.Item
            label="当前 Owner 密码"
            name="currentPassword"
            rules={[{ required: true, message: "请输入当前 Owner 密码确认删除" }]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
          </Form.Item>
          {user.totpEnabled ? (
            <Form.Item
              label="当前动态验证码"
              name="totp"
              rules={[{ required: true }, { len: 6, message: "请输入 6 位动态码" }]}
            >
              <Input
                prefix={<SafetyCertificateOutlined />}
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
              />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>
    </Drawer>
  );
}
